import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Phone, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/subscribers`, { email });
      toast.success("Success! You've been subscribed to our newsletter.");
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("This email is already subscribed.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#2C2C2C] text-[#FAF9F7]">
      {/* Instagram CTA Strip */}
      <div className="py-10 text-center" style={{ backgroundColor: '#DE92A9' }}>
        <a
          href="https://www.instagram.com/idomagofficial/"
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex flex-col items-center gap-3"
          data-testid="footer-instagram-cta"
        >
          <Instagram size={28} className="text-white/90" />
          <p className="text-white text-lg tracking-wide" style={{ fontFamily: '"Playfair Display", serif' }}>
            Follow our journey
          </p>
          <p className="text-white/70 text-sm tracking-[0.2em] group-hover:text-white transition-colors">
            @idomagofficial
          </p>
        </a>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <h3
              className="text-4xl mb-1"
              style={{ fontFamily: '"Playfair Display", serif', color: '#DE92A9' }}
            >
              I DO
            </h3>
            <p className="text-[10px] tracking-[0.2em] mb-5 uppercase" style={{ color: '#757B7F' }}>
              Bridal Magazine Malta
            </p>
            <p className="text-[#A0978A] text-sm leading-relaxed mb-6">
              Celebrating love stories and bringing wedding dreams to life across Malta since 2016.
            </p>
            <div className="flex gap-4">
              <a href="https://www.facebook.com/IDOBridalMagazineMalta" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-[#4C4C4C] flex items-center justify-center hover:border-[#DE92A9] hover:text-[#DE92A9] transition-colors text-[#A0978A]" data-testid="footer-facebook-link">
                <Facebook size={16} />
              </a>
              <a href="https://www.instagram.com/idomagofficial/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-[#4C4C4C] flex items-center justify-center hover:border-[#DE92A9] hover:text-[#DE92A9] transition-colors text-[#A0978A]" data-testid="footer-instagram-link">
                <Instagram size={16} />
              </a>
            </div>
          </div>

          {/* Explore */}
          <div>
            <h4 className="text-[11px] tracking-[0.2em] uppercase mb-5 font-semibold" style={{ color: '#DE92A9' }}>Explore</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/wedding-stories" className="text-[#A0978A] hover:text-white transition-colors">Wedding Stories</Link></li>
              <li><Link to="/the-magazine" className="text-[#A0978A] hover:text-white transition-colors">The Magazine</Link></li>
              <li><Link to="/tips-inspiration" className="text-[#A0978A] hover:text-white transition-colors">Tips & Inspiration</Link></li>
              <li><Link to="/partners" className="text-[#A0978A] hover:text-white transition-colors">Our Partners</Link></li>
              <li><Link to="/countdown" className="text-[#A0978A] hover:text-white transition-colors">Wedding Countdown</Link></li>
              <li><Link to="/checklist" className="text-[#A0978A] hover:text-white transition-colors" data-testid="footer-checklist-link">Wedding Checklist</Link></li>
            </ul>
          </div>

          {/* Get Involved */}
          <div>
            <h4 className="text-[11px] tracking-[0.2em] uppercase mb-5 font-semibold" style={{ color: '#DE92A9' }}>Get Involved</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about" className="text-[#A0978A] hover:text-white transition-colors">About I DO</Link></li>
              <li><Link to="/submit-wedding" className="text-[#A0978A] hover:text-white transition-colors">Submit Your Wedding</Link></li>
              <li><Link to="/advertise" className="text-[#A0978A] hover:text-white transition-colors">Advertise With Us</Link></li>
              <li><Link to="/contact" className="text-[#A0978A] hover:text-white transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-[11px] tracking-[0.2em] uppercase mb-5 font-semibold" style={{ color: '#DE92A9' }}>Stay Inspired</h4>
            <p className="text-[#A0978A] text-sm mb-4 leading-relaxed">
              Planning tips, vendor spotlights, and bridal trends delivered to your inbox.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <Input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#3A3A3A] border-[#4C4C4C] text-white placeholder:text-[#6B6B6B] rounded-none h-11 text-sm focus:border-[#DE92A9]"
                required
                data-testid="footer-newsletter-input"
              />
              <Button
                type="submit"
                className="w-full text-white rounded-none h-11 text-xs tracking-[0.15em] font-semibold"
                style={{ backgroundColor: '#DE92A9' }}
                disabled={loading}
                data-testid="footer-newsletter-submit"
              >
                {loading ? 'SUBSCRIBING...' : 'SUBSCRIBE'}
              </Button>
            </form>
          </div>
        </div>

        {/* Contact Row */}
        <div className="border-t border-[#3A3A3A] py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-[#A0978A]">
            <div className="flex items-center gap-6">
              <a href="tel:+35699801504" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-phone">
                <Phone size={14} />
                +356 9980 1504
              </a>
              <a href="mailto:idobridalmagazinemalta@gmail.com" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-email">
                <Mail size={14} />
                idobridalmagazinemalta@gmail.com
              </a>
            </div>
            <div className="text-center md:text-right text-xs">
              <p className="text-[#757B7F]">Standard Publications Ltd</p>
              <p className="text-[#5A5A5A]">Standard House, Birkirkara Hill, St. Julian's, STJ 1149</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#3A3A3A] pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-[11px] tracking-wide" style={{ color: '#5A5A5A' }}>
            <p>&copy; 2026 I DO Bridal Magazine Malta. All rights reserved.</p>
            <Link to="/privacy-policy" className="hover:text-[#DE92A9] transition-colors" data-testid="footer-privacy-link">
              Privacy & Cookie Policy
            </Link>
            <div className="flex items-center gap-1 text-[#5A5A5A]">
              <span>Made with</span>
              <Heart size={10} fill="#DE92A9" className="text-[#DE92A9]" />
              <span>in Malta</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
